import {
  OKTAUserSeviceFlagEnum,
  OKTAUserTypeEnum,
} from "@app/hooks/useUserInfo";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import fetchBHApi from "@helper/fetchBHApi";
import { MYSPH_NOT_AVAILABLE } from "@helper/getEnvVariables";
import hasServiceFlag from "@helper/hasServiceFlag";
import { getUserType } from "@src/app/hooks/useUserInfo.ts";
import { type ProcessedArticleData } from "@transformer/useOSResponse";
import { ProcessedArticleBody } from "@typings/Opensearch.d";
import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";

import { ArticleParagraphProps } from "../ArticleParagraph";
import GetLimitedParagraphsFromArticleBody from "../GetLimitedParagraphsFromArticleBody";

export function useAccessControl(
  props: Pick<
    ArticleParagraphProps,
    | "articleId"
    | "contentAccess"
    | "articleBody"
    | "uniqueSectionName"
    | "isInfiniteScrollArticle"
    | "freeArticle"
  >,
) {
  const {
    articleId,
    contentAccess,
    articleBody,
    uniqueSectionName,
    isInfiniteScrollArticle,
    freeArticle,
  } = props;
  const [showSubBanner, setShowSubBanner] = useState(false);
  const [showGenGSubBanner, setShowGenGSubBanner] = useState(false);
  const [showRegBanner, setShowRegBanner] = useState(false);
  const [showReg2Banner, setShowReg2Banner] = useState<boolean>(false);
  const [showNormalBanner, setShowNormalBanner] = useState(false);
  const [translatedArticle, setTranslatedArticle] = useState<
    ProcessedArticleData | undefined
  >(freeArticle);
  const [trimmedParagraphs, setTrimmedParagraphs] =
    useState<ProcessedArticleBody | null>(
      articleBody && contentAccess !== LabelCardIconVariety.FREE
        ? GetLimitedParagraphsFromArticleBody(articleBody, 2)
        : articleBody,
    );

  const fetchFullArticleBody = useCallback(() => {
    fetchBHApi<ProcessedArticleData[]>(`article/${articleId}`, "GET")
      .then((data) => {
        setTrimmedParagraphs(data[0].articleBody);
        setTranslatedArticle(data[0]);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [articleId]);

  useEffect(() => {
    // Get the user type (e.g., y-sub, y-anoy, etc.)
    const userType = getUserType();
    const mysphidCookie = Cookies.get("mysph-sid");
    console.log("contentAccess:", contentAccess);
    console.log("freeArticle?.variant:", freeArticle?.variant);
    // Condition 1: If the content requires a PREMIUM subscription,
    // the section is "gen-g", and the user doesn't have the "bh_geng" service flag,
    // show the Gen-G subscription banner and trim the article body to show only one paragraph.
    if (parseInt(MYSPH_NOT_AVAILABLE)) {
      fetchFullArticleBody();
      // Condition 2: If the user is anonymous,
      // free story and regwall2 variant is applicable, show hardregwall banner.
    } else if (
      contentAccess === LabelCardIconVariety.FREE &&
      !mysphidCookie &&
      freeArticle?.variant === "partial"
    ) {
      console.log("partial variant is ok");
      setShowReg2Banner(true);
      // Condition 3: If the content requires a PREMIUM subscription,
      // the section is "gen-g", and the user doesn't have the "bh_geng" service flag,
      // show the Gen-G subscription banner and trim the article body to show only one paragraph.
    } else if (
      contentAccess === LabelCardIconVariety.PREMIUM &&
      uniqueSectionName === "gen-g" &&
      !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_GENG)
    ) {
      setShowGenGSubBanner(true);
      setTrimmedParagraphs(
        articleBody && GetLimitedParagraphsFromArticleBody(articleBody, 1),
      );

      // Condition 4: If the content requires a PREMIUM subscription,
      // but the user is not a "y-sub" type, show the generic subscription banner.
    } else if (
      contentAccess === LabelCardIconVariety.PREMIUM &&
      uniqueSectionName !== "gen-g" &&
      (userType !== OKTAUserTypeEnum.SUBSCRIBER ||
        !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE))
    ) {
      setShowSubBanner(true);

      // Condition 5: If the content is for REGISTERED_USER,
      // and the user is either anonymous ("y-anoy") or the user type is undefined,
      // show the registration banner.
    } else if (
      contentAccess === LabelCardIconVariety.REGISTERED_USER &&
      userType === OKTAUserTypeEnum.ANONYMOUS
    ) {
      setShowRegBanner(true);

      // Condition 6: For gen g ala carte cases where the user is not a "y-anoy" type
      // and has service flag bh_geng, show the normal banner and set the
      // full article body to be displayed.
    } else if (
      uniqueSectionName === "gen-g" &&
      userType !== OKTAUserTypeEnum.ANONYMOUS &&
      hasServiceFlag(OKTAUserSeviceFlagEnum.BH_GENG)
    ) {
      fetchFullArticleBody();
      if (
        userType !== OKTAUserTypeEnum.SUBSCRIBER ||
        !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE)
      ) {
        setShowNormalBanner(true);
      }

      // Condition 7: For all other cases where the user is not a "y-sub" type,
      // show the normal banner and set the full article body to be displayed.
    } else if (
      userType !== OKTAUserTypeEnum.SUBSCRIBER ||
      !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE)
    ) {
      if (
        contentAccess === LabelCardIconVariety.REGISTERED_USER &&
        !isInfiniteScrollArticle
      ) {
        fetchFullArticleBody();
      } else {
        setTrimmedParagraphs(articleBody);
      }
      setShowNormalBanner(true);

      // Default: If none of the above conditions are met,
      // set the full article body to be displayed and hide all banners.
    } else {
      if (
        (contentAccess === LabelCardIconVariety.PREMIUM ||
          contentAccess === LabelCardIconVariety.REGISTERED_USER) &&
        !isInfiniteScrollArticle
      ) {
        fetchFullArticleBody();
      } else {
        setTrimmedParagraphs(articleBody);
      }
      setShowGenGSubBanner(false);
      setShowSubBanner(false);
      setShowRegBanner(false);
      setShowNormalBanner(false);
      setShowReg2Banner(false);
    }
  }, [
    contentAccess,
    articleBody,
    uniqueSectionName,
    fetchFullArticleBody,
    isInfiniteScrollArticle,
    freeArticle?.variant,
  ]);

  return {
    trimmedParagraphs,
    showSubBanner,
    showGenGSubBanner,
    showRegBanner,
    showNormalBanner,
    showReg2Banner,
    translatedArticle,
  };
}
